import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  CalendarCheck,
  Plug,
  RefreshCw,
  Unplug,
  Copy,
  CheckCircle2,
  KeyRound,
  Loader2,
  Speech,
  BellRing,
  BellOff,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { formatDateTime } from "@/lib/format";
import { pushSupported, getPushSubscription, enablePush, disablePush } from "@/lib/push";

const CopyField = ({ value, testId }) => (
  <div className="flex items-center gap-2 mt-1">
    <code className="flex-1 text-xs bg-stone-100 border border-stone-200 rounded px-2 py-1.5 truncate font-mono">
      {value}
    </code>
    <Button
      type="button"
      variant="outline"
      size="icon"
      className="h-8 w-8 shrink-0"
      data-testid={testId}
      onClick={() => {
        navigator.clipboard.writeText(value);
        toast.success("Copiado!");
      }}
    >
      <Copy className="h-3.5 w-3.5" />
    </Button>
  </div>
);

export default function Integrations() {
  const { user } = useAuth();
  const isAdmin = ["super_admin", "company_admin"].includes(user?.role);
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [creds, setCreds] = useState({ client_id: "", client_secret: "" });
  const [credsInfo, setCredsInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);

  const origin = window.location.origin;

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/integrations/google/status");
      setStatus(data);
    } catch (err) {
      console.error("gcal status failed", err);
    }
    if (isAdmin) {
      try {
        const { data } = await api.get("/integrations/google/credentials");
        setCredsInfo(data);
      } catch (err) {
        console.error("gcal creds failed", err);
      }
    }
  }, [isAdmin]);

  useEffect(() => {
    load();
    (async () => {
      try {
        const sub = await getPushSubscription();
        setPushEnabled(!!sub);
      } catch (err) {
        console.warn("push status check failed", err);
      }
    })();
  }, [load]);

  const togglePush = async () => {
    setPushBusy(true);
    try {
      if (pushEnabled) {
        await disablePush();
        setPushEnabled(false);
        toast.success("Notificações push desativadas neste dispositivo");
      } else {
        await enablePush();
        setPushEnabled(true);
        toast.success("Notificações push ativadas! Você será avisado nos horários dos checklists.");
      }
    } catch (err) {
      toast.error(err?.message || "Falha ao alterar notificações push");
    } finally {
      setPushBusy(false);
    }
  };

  const testPush = async () => {
    setPushBusy(true);
    try {
      await api.post("/push/test");
      toast.success("Notificação de teste enviada — confira a bandeja do dispositivo");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao enviar teste");
    } finally {
      setPushBusy(false);
    }
  };

  useEffect(() => {
    if (searchParams.get("connected") === "1") {
      toast.success("Google Agenda conectada com sucesso!");
      setSearchParams({}, { replace: true });
      load();
    } else if (searchParams.get("error")) {
      toast.error(searchParams.get("error"));
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams, load]);

  const saveCreds = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/integrations/google/credentials", creds);
      toast.success("Credenciais salvas");
      setCreds({ client_id: "", client_secret: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao salvar credenciais");
    } finally {
      setBusy(false);
    }
  };

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/integrations/google/auth-url", {
        origin_url: origin,
      });
      window.location.href = data.authorization_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao iniciar conexão");
      setBusy(false);
    }
  };

  const syncNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/integrations/google/sync");
      toast.success(
        `Sincronizado: ${data.events_created} evento(s) novo(s), ${data.upcoming_events} futuro(s) na agenda`
      );
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao sincronizar");
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!confirm("Desconectar sua Google Agenda? Os eventos futuros deixarão de ser criados.")) return;
    setBusy(true);
    try {
      await api.post("/integrations/google/disconnect");
      toast.success("Conta desconectada");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao desconectar");
    } finally {
      setBusy(false);
    }
  };

  const credsConfigured = status?.credentials_configured;

  return (
    <Layout
      title="Integrações"
      subtitle="Conecte sua Google Agenda para receber os checklists agendados — e ouvir pela Alexa"
    >
      <div data-testid="integrations-page" className="max-w-3xl space-y-6">
        {/* Connection status */}
        <Card className="p-6 rounded-xl border-stone-200 bg-white">
          <div className="flex items-start gap-4">
            <div className="h-11 w-11 rounded-lg bg-emerald-50 text-emerald-800 flex items-center justify-center shrink-0">
              <CalendarCheck className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-display text-lg font-bold tracking-tight">Google Agenda</h3>
                {status?.connected ? (
                  <Badge className="bg-emerald-700 text-white border-none" data-testid="gcal-connected-badge">
                    <CheckCircle2 className="h-3 w-3 mr-1" /> Conectada
                  </Badge>
                ) : (
                  <Badge variant="outline" className="border-stone-300 text-stone-600">
                    Não conectada
                  </Badge>
                )}
              </div>
              <p className="text-sm text-stone-500 font-body mt-1">
                {user?.role === "company_admin"
                  ? "Como Admin da empresa, sua agenda receberá os checklists agendados de TODOS os usuários."
                  : "Sua agenda receberá apenas os checklists agendados visíveis ao seu perfil e unidade."}{" "}
                Cada evento traz um link direto — um toque abre o app já na execução do checklist.
              </p>

              {status?.connected ? (
                <div className="mt-4 space-y-2">
                  <div className="text-sm font-body">
                    <span className="text-stone-500">Conta:</span>{" "}
                    <span className="font-semibold" data-testid="gcal-email">{status.google_email}</span>
                  </div>
                  <div className="text-xs text-stone-500 font-body">
                    Última sincronização:{" "}
                    {status.last_sync_at ? formatDateTime(status.last_sync_at) : "—"} ·{" "}
                    {status.upcoming_events} evento(s) futuro(s) na agenda
                  </div>
                  <div className="flex gap-2 pt-1">
                    <Button
                      size="sm"
                      onClick={syncNow}
                      disabled={busy}
                      className="bg-emerald-800 hover:bg-emerald-900 text-white"
                      data-testid="gcal-sync-now"
                    >
                      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
                      Sincronizar agora
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={disconnect}
                      disabled={busy}
                      className="text-rose-700 border-rose-200 hover:bg-rose-50"
                      data-testid="gcal-disconnect"
                    >
                      <Unplug className="h-3.5 w-3.5 mr-1.5" /> Desconectar
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="mt-4">
                  <Button
                    onClick={connect}
                    disabled={busy || !credsConfigured}
                    className="bg-emerald-800 hover:bg-emerald-900 text-white"
                    data-testid="gcal-connect-btn"
                  >
                    <Plug className="h-4 w-4 mr-2" /> Conectar Google Agenda
                  </Button>
                  {!credsConfigured && (
                    <p className="text-xs text-amber-700 font-body mt-2" data-testid="gcal-no-creds-warning">
                      {isAdmin
                        ? "Configure as credenciais do Google Cloud abaixo antes de conectar."
                        : "As credenciais do Google ainda não foram configuradas. Peça ao Admin da empresa."}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* Push notifications */}
        <Card className="p-6 rounded-xl border-stone-200 bg-white" data-testid="push-card">
          <div className="flex items-start gap-4">
            <div className="h-11 w-11 rounded-lg bg-emerald-50 text-emerald-800 flex items-center justify-center shrink-0">
              <BellRing className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-display text-lg font-bold tracking-tight">Notificações Push</h3>
                {pushEnabled ? (
                  <Badge className="bg-emerald-700 text-white border-none" data-testid="push-enabled-badge">
                    <CheckCircle2 className="h-3 w-3 mr-1" /> Ativas neste dispositivo
                  </Badge>
                ) : (
                  <Badge variant="outline" className="border-stone-300 text-stone-600">
                    Desativadas
                  </Badge>
                )}
              </div>
              <p className="text-sm text-stone-500 font-body mt-1">
                Receba um aviso no celular na hora exata de cada checklist agendado — mesmo com o
                app fechado. O aviso abre direto na execução.
              </p>
              {!pushSupported() ? (
                <p className="text-xs text-amber-700 font-body mt-3" data-testid="push-unsupported">
                  Este navegador não suporta notificações push. No iPhone, instale o app na tela
                  inicial (Compartilhar → Adicionar à Tela de Início) e ative por lá.
                </p>
              ) : (
                <div className="flex gap-2 mt-4">
                  <Button
                    size="sm"
                    onClick={togglePush}
                    disabled={pushBusy}
                    className={
                      pushEnabled
                        ? "bg-white border border-rose-200 text-rose-700 hover:bg-rose-50"
                        : "bg-emerald-800 hover:bg-emerald-900 text-white"
                    }
                    data-testid="push-toggle-btn"
                  >
                    {pushBusy ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                    ) : pushEnabled ? (
                      <BellOff className="h-3.5 w-3.5 mr-1.5" />
                    ) : (
                      <BellRing className="h-3.5 w-3.5 mr-1.5" />
                    )}
                    {pushEnabled ? "Desativar" : "Ativar notificações"}
                  </Button>
                  {pushEnabled && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={testPush}
                      disabled={pushBusy}
                      data-testid="push-test-btn"
                    >
                      Enviar teste
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* Admin: Google Cloud credentials */}
        {isAdmin && (
          <Card className="p-6 rounded-xl border-stone-200 bg-white" data-testid="gcal-creds-card">
            <div className="flex items-center gap-2">
              <KeyRound className="h-4 w-4 text-emerald-700" />
              <h3 className="font-display text-lg font-bold tracking-tight">
                Credenciais do Google Cloud
              </h3>
              {credsInfo?.configured && (
                <Badge className="bg-emerald-700 text-white border-none">Configurado</Badge>
              )}
            </div>
            <p className="text-sm text-stone-500 font-body mt-1 mb-4">
              {credsInfo?.configured
                ? `Client ID atual: ${credsInfo.client_id_masked} — salve novamente para substituir.`
                : "Cole aqui o Client ID e o Client Secret criados no Google Cloud Console (tutorial abaixo)."}
            </p>
            <form onSubmit={saveCreds} className="space-y-3">
              <div>
                <Label className="text-sm font-body">Client ID *</Label>
                <Input
                  value={creds.client_id}
                  onChange={(e) => setCreds({ ...creds, client_id: e.target.value })}
                  placeholder="xxxxxxxx.apps.googleusercontent.com"
                  required
                  className="mt-1 bg-white font-mono text-xs"
                  data-testid="gcal-client-id"
                />
              </div>
              <div>
                <Label className="text-sm font-body">Client Secret *</Label>
                <Input
                  type="password"
                  value={creds.client_secret}
                  onChange={(e) => setCreds({ ...creds, client_secret: e.target.value })}
                  placeholder="GOCSPX-..."
                  required
                  className="mt-1 bg-white font-mono text-xs"
                  data-testid="gcal-client-secret"
                />
              </div>
              <Button
                type="submit"
                disabled={busy}
                className="bg-emerald-800 hover:bg-emerald-900 text-white"
                data-testid="gcal-save-creds"
              >
                Salvar credenciais
              </Button>
            </form>
          </Card>
        )}

        {/* Tutorial */}
        <Card className="p-6 rounded-xl border-stone-200 bg-white" data-testid="gcal-tutorial">
          <h3 className="font-display text-lg font-bold tracking-tight">
            Mini-tutorial: como obter as credenciais
          </h3>
          <ol className="mt-4 space-y-4 text-sm font-body text-stone-700 list-none">
            {[
              <>Acesse <a className="text-emerald-800 underline" href="https://console.cloud.google.com" target="_blank" rel="noreferrer">console.cloud.google.com</a> e crie um projeto (ex: "Ultra").</>,
              <>No menu ☰ → <strong>APIs e serviços → Biblioteca</strong>, busque <strong>"Google Calendar API"</strong> e clique em <strong>Ativar</strong>.</>,
              <>Em <strong>APIs e serviços → Tela de permissão OAuth</strong>: escolha tipo <strong>Externo</strong>, preencha o nome do app e seu e-mail. Em <strong>Usuários de teste</strong>, adicione os e-mails Google de quem vai conectar a agenda.</>,
              <>Em <strong>APIs e serviços → Credenciais → Criar credenciais → ID do cliente OAuth</strong>, escolha tipo <strong>Aplicativo da Web</strong>.</>,
              <>
                Preencha os campos com os valores abaixo:
                <div className="mt-2">
                  <Label className="text-xs text-stone-500">Origens JavaScript autorizadas</Label>
                  <CopyField value={origin} testId="gcal-copy-origin" />
                </div>
                <div className="mt-2">
                  <Label className="text-xs text-stone-500">URIs de redirecionamento autorizados</Label>
                  <CopyField value={`${origin}/api/oauth/calendar/callback`} testId="gcal-copy-redirect" />
                </div>
              </>,
              <>Clique em <strong>Criar</strong> e copie o <strong>Client ID</strong> e o <strong>Client Secret</strong> gerados. {isAdmin ? "Cole-os no cartão \"Credenciais do Google Cloud\" acima e salve." : "Envie-os ao Admin da empresa para configurar."}</>,
              <>Volte aqui e clique em <strong>Conectar Google Agenda</strong> — autorize com sua conta Google. Pronto: os checklists agendados aparecerão na sua agenda com lembretes nos horários definidos.</>,
            ].map((step, i) => (
              <li key={i} className="flex gap-3">
                <span className="h-6 w-6 rounded-full bg-emerald-800 text-white text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                  {i + 1}
                </span>
                <div className="flex-1 min-w-0">{step}</div>
              </li>
            ))}
          </ol>
        </Card>

        {/* Alexa */}
        <Card className="p-6 rounded-xl border-stone-200 bg-stone-950 text-stone-100">
          <div className="flex items-start gap-4">
            <div className="h-11 w-11 rounded-lg bg-emerald-500/20 text-emerald-400 flex items-center justify-center shrink-0">
              <Speech className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-display text-lg font-bold tracking-tight text-white">
                E a Alexa?
              </h3>
              <p className="text-sm font-body text-stone-300 mt-1">
                Com a agenda conectada, vincule a mesma conta Google à Alexa e ela anuncia os
                checklists nos horários:
              </p>
              <ol className="mt-3 space-y-1.5 text-sm font-body text-stone-300 list-decimal list-inside">
                <li>Abra o app <strong className="text-white">Amazon Alexa</strong> → Mais → Configurações → <strong className="text-white">Calendário e E-mail</strong>.</li>
                <li>Toque em <strong className="text-white">Adicionar conta → Google</strong> e entre com a mesma conta conectada aqui.</li>
                <li>Ative os <strong className="text-white">avisos de calendário</strong> no dispositivo Echo desejado.</li>
                <li>Teste: <em>"Alexa, o que tem na minha agenda hoje?"</em></li>
              </ol>
            </div>
          </div>
        </Card>
      </div>
    </Layout>
  );
}
